import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import { useStyles } from '../../style/views'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import InputLabel from '@mui/material/InputLabel'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import * as _ from 'underscore'
import store from '../../redux/store'
import FormLabel from '@mui/material/FormLabel'
import RadioGroup from '@mui/material/RadioGroup'
import FormGroup from '@mui/material/FormGroup'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import { GreenRadio, RedRadio, YellowRadio, WhiteOutlineCheckbox } from '../Support/CustomElements'
import { useAuth0 } from '../../contexts/auth0-context'
import { uploadFile } from '../../API/PostRequests'
import ReactCountryFlag from 'react-country-flag'
import { useUploadFileAndCreateDraftMutation } from '../../shared/queries/useUploadFileAndCreateDraftMutation'
import { useProcessEvidenceForMaiaMutation } from '../../shared/queries/useProcessEvidenceForMaiaMutation'
import { useGetOrganizationQuery } from '../../shared/queries/useGetOrganizationQuery'
import { useGetSettingsQuery } from '../../shared/queries/useGetSettingsQuery'
import { useProcessPDFWithAIMutation } from '../../shared/queries/useProcessPDFWithAIMutation'
import { useSaveKESStatementsMutation } from '../../shared/queries/useSaveKESStatementsMutation'
import BatchDraftReviewDialog from '../Evidence/BatchDraftReviewDialog'
import Dropzone from './Dropzone'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Box from '@mui/material/Box'
import { getISOStringFromCalendarDate, timeout } from '../../utilities/helpers'
import { Icon, Skeleton, stepLabelClasses } from '@mui/material'
import { styled } from '@mui/system'
import AtlasAIButton from '../../shared/components/AtlasAIButton'
import FileIcon from '../../assets/images/file-icon.svg'
import { ReactComponent as ActionsIcon } from '../../assets/images/actions/actions.svg'
import { ReactComponent as AddCircleIcon } from '../../assets/images/add-circle.svg'
import { v4 as uuidv4 } from 'uuid'
import Popover from '@mui/material/Popover'
import Chip from '@mui/material/Chip'
import PubMedView from '../Evidence/APIs/PubMed/PubMedView'
import useValueMessageDisplayName from '../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import useEventLogger from '../../shared/hooks/useEventLogger'
import useStrategicImperativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import { PUBLICATIONS } from '../../shared/constants/publications'
import { Alert } from '../../shared/components/Alert'
import { FIGMA_ACCENT_COLORS, FIGMA_DARK_COLORS, FIGMA_LIGHT_COLORS } from '../../style/colors'
import { useIsRoadmapReadonly } from '../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../shared/hooks/useIsSnapshotMode'
import ActivityForm from '../Activities/ActivityForm'
import { useCreateActivityMutation } from '../../shared/queries/useCreateActivityMutation'
import { useAutoLinkActivityToExternalSource } from '../../shared/queries/useAutoLinkActivityToExternalSource'
import { useInvalidateActivitiesQuery } from '../../shared/queries/useInvalidateActivitiesQuery'
import { useInvalidateGetValueMessageLinkedDataQuery } from '../../shared/queries/useInvalidateGetValueMessageLinkedDataQuery'
import Snackbar from '@mui/material/Snackbar'
import MuiAlert from '@mui/material/Alert'
import StrategicImperativeForm from '../StrategicImperatives/StrategicImperativeForm'
import { useCreateStrategicImperativeMutation } from '../../shared/queries/strategicImperatives'

export const Logo = () => (
    <Icon>
        <img src={FileIcon} height={18} width={18} style={{ marginLeft: '10px', marginBottom: '3px' }} />
    </Icon>
)

const steps = ['Extracting Text', 'Processing Data', 'Waiting For Server']

// These create in forms that carry their own Cancel and Save buttons.
const FORMS_WITH_OWN_ACTIONS = ['activity', 'strategicimperative']

const DRAFT_STATUS_DISPLAY = {
    draft: 'Draft',
    'in review': 'In Review',
    rejected: 'Rejected',
    accepted: 'Accepted',
}

const DRAFT_STATUS_ACTIONS = [
    { label: 'Accept', value: 'accepted' },
    { label: 'Mark as In Review', value: 'in review' },
    { label: 'Mark as Draft', value: 'draft' },
    { label: 'Rejected', value: 'rejected' },
]

const DRAFT_STATUS_BADGE_COLOR = {
    draft: 'figma.grayscale.tier4',
    'in review': 'figma.accentColors.orange',
    rejected: 'figma.accentColors.red',
    accepted: 'figma.accentColors.green',
}

const ColorlibStepLabel = styled(StepLabel)(({ theme }) => ({
    [`& .${stepLabelClasses.label}`]: {
        [`&.${stepLabelClasses.completed}`]: {
            color: theme.palette.figma.primary,
        },
        [`&.${stepLabelClasses.active}`]: {
            color: theme.palette.figma.primaryHover,
        },
        [`&.${stepLabelClasses.error}`]: {
            color: theme.palette.figma.accentColors.red,
        },
        color: theme.palette.figma.accentColors.tier1,
    },
    [`& .MuiStepIcon-root`]: {
        [`&.Mui-error`]: {
            color: theme.palette.figma.accentColors.red,
        },
    },
}))

const SKELETON_COLOR = 'grey.220'

let categories = [
    '---',
    'Journal Pub',
    'Congress Pub',
    'HCEI502(a)',
    'PIE',
    'Field Tool',
    'Label',
    'Dossier',
    'Abstract',
    'Poster',
    'Manuscript',
]

const acceptedFileTypes = {
    'application/pdf': ['.pdf'],
}

const CreateButton = forwardRef((props, ref) => {
    const classes = useStyles()
    const [settings, setSettings] = useState({})
    const { isAuthenticated, getTokenSilently } = useAuth0()
    const { logEvent, ACTIONS } = useEventLogger()

    // @REVIEW - these 2 checks need a separate hook
    const isReadonlyMode = useIsRoadmapReadonly()
    const isSnapshotMode = useIsSnapshotMode()
    const canCreate = !isReadonlyMode && !isSnapshotMode

    const { data: orgData } = useGetOrganizationQuery()
    const { data: settingsData } = useGetSettingsQuery()
    const { mutateAsync: processPDFWithAIAsync } = useProcessPDFWithAIMutation()
    const { mutateAsync: saveKESStatementsAsync } = useSaveKESStatementsMutation()
    const { mutateAsync: uploadFileAndCreateDraftAsync } = useUploadFileAndCreateDraftMutation()
    const { mutate: processEvidenceForMaia } = useProcessEvidenceForMaiaMutation()
    const { mutateAsync: createActivityMutation } = useCreateActivityMutation()
    const { autoLinkActivityToExternalSource } = useAutoLinkActivityToExternalSource()
    const { invalidateActivitiesQuery } = useInvalidateActivitiesQuery()
    const { invalidateGetValueMessageLinkedDataQuery } = useInvalidateGetValueMessageLinkedDataQuery()
    const [activitySaveError, setActivitySaveError] = useState(null)

    const { createNew } = props

    const [open, setOpen] = useState(false)
    const [status, setStatus] = useState('Supported')
    const [name, setName] = useState('')
    const [priority, setPriority] = useState('---')
    const [vmDescription, setVMDescription] = useState('')
    const [newAudiences, setNewAudiences] = useState({})
    const [audiences, setAudiences] = useState({})
    const [internalStakeholders, setInternalStakeholders] = useState({})
    const [newInternalStakeholders, setNewInternalStakeholders] = useState({})
    const [newLocalMarkets, setNewLocalMarkets] = useState([])
    const [openNewLocalVMDialog, setOpenNewLocalVMDialog] = useState(false)

    const [isAIEnabled, setIsAIEnabled] = React.useState(true)
    const [isAIAutoImportEnabled, setIsAIAutoImportEnabled] = React.useState(true)
    const [didGenerate, setDidGenerate] = React.useState(false)
    const [stepper, setStepper] = React.useState(0)
    const [isStepFailed, setIsStepFailed] = useState([])
    const [errorMessage, setErrorMessage] = useState('')

    const [evidenceName, setEvidenceName] = useState('')
    const [evidenceAbstract, setEvidenceAbstract] = useState('')
    const [evidenceYear, setEvidenceYear] = useState(2025)
    const [evidenceIsInternal, setEvidenceIsInternal] = useState(false)
    const [evidenceCategory, setEvidenceCategory] = useState('---')
    const [evidenceCategories, setEvidenceCategories] = useState([])
    const [file, setFile] = useState({})
    const [batchFiles, setBatchFiles] = useState([])
    const [batchResults, setBatchResults] = useState([])
    const [batchPhase, setBatchPhase] = useState('idle') // 'idle' | 'processing'
    const [batchProcessingIndex, setBatchProcessingIndex] = useState(0)
    const [batchReviewOpen, setBatchReviewOpen] = useState(false)
    const [draftStatus, setDraftStatus] = useState('draft')
    const [statusMenuAnchor, setStatusMenuAnchor] = useState(null)
    const [localKES, setLocalKES] = useState([])

    function handleAddKES() {
        setLocalKES((prev) => [...prev, { id: uuidv4(), statement: '', figures: [] }])
    }

    function handleKESTextChange(id, text) {
        setLocalKES((prev) => prev.map((k) => (k.id === id ? { ...k, statement: text } : k)))
    }

    function handleKESFileSelect(e, id) {
        const files = Array.from(e.target.files || []).filter((f) => {
            const ext = f.name.split('.').pop()?.toLowerCase()
            return ext === 'png' || ext === 'jpg' || ext === 'jpeg'
        })
        const readers = files.map(
            (f) =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(f)
                    reader.onload = () => resolve(reader.result)
                    reader.onerror = reject
                })
        )
        Promise.all(readers).then((base64Array) => {
            setLocalKES((prev) =>
                prev.map((k) => (k.id === id ? { ...k, figures: [...k.figures, ...base64Array] } : k))
            )
        })
    }

    async function saveLocalKES(draftId) {
        await saveKESStatementsAsync({ draftId, localKES })
    }

    const [link, setLink] = useState('')
    const [author, setAuthor] = useState('')
    const [uniqueIdentifier, setUniqueIdentifier] = useState('')
    const [evidencePopulation, setEvidencePopulation] = useState('---')
    const [publicationType, setPublicationType] = useState('---')
    const [nickname, setNickname] = useState('')
    const [citation, setCitation] = useState('')
    const [shortCitation, setShortCitation] = useState('')
    const [longCitation, setLongCitation] = useState('')
    const [pubmedId, setPubmedId] = useState('')

    const [gapTitle, setGapTitle] = useState('')
    const [gapPriority, setGapPriority] = useState('medium')
    const [gapNotes, setGapNotes] = useState('')
    const [gapSummary, setGapSummary] = useState('')

    const [leverValue, setLeverValue] = useState([])
    const [newLeverValue, setNewLeverValue] = useState([])

    const [deliverableTitle, setDeliverableTitle] = useState('')
    const [deliverableType, setDeliverableType] = useState('None')
    const [deliverableDate, setDeliverableDate] = useState(null)
    const [deliverableIsComplete, setDeliverableIsComplete] = useState(false)

    const [isSubmittingLocal, setIsSubmittingLocal] = useState(false)
    const isSubmittingExtInt = props.isLoading || isSubmittingLocal

    const [tracelinkSlug, setTracelinkSlug] = useState('')
    const [tracelinkOwner, setTracelinkOwner] = useState('')

    const valueMessageDisplayName = useValueMessageDisplayName()
    const strategicImperativeDisplayName = useStrategicImperativeDisplayName()
    const { mutateAsync: createStrategicImperative } = useCreateStrategicImperativeMutation()

    const activityDisplayName = _.isEmpty(store.getState().settings)
        ? ''
        : store.getState().settings.displayNames.activity
    const evidenceDisplayName = _.isEmpty(store.getState().settings)
        ? ''
        : store.getState().settings.displayNames.evidence
    const activitiesDisplayName = _.isEmpty(store.getState().settings)
        ? ''
        : store.getState().settings.displayNames.activities

    useEffect(() => {
        if (orgData?.isAIEnabled) setIsAIEnabled(true)
    }, [orgData])

    useEffect(() => {
        const data = settingsData
        if (!data) return
        setSettings(data)

        setNewLocalMarkets(flattenMarkets().map((market) => market.code))

        updateState(data)

        // Grabbing VM Audiences data and setting to obj
        let incomingAudiences = data.vmAudiences
        let audiencesObj = {}
        for (let i = 0; i < incomingAudiences.length; i++) {
            audiencesObj[incomingAudiences[i]] = false
        }
        setNewAudiences(audiencesObj)
        setAudiences(audiencesObj)

        // Grabbing VM Internal Stakeholders data and setting to obj
        let incomingInternalStakeholders = data.vmInternalStakeholders || []
        let internalStakeholdersObj = {}
        for (let i = 0; i < incomingInternalStakeholders.length; i++) {
            internalStakeholdersObj[incomingInternalStakeholders[i]] = false
        }
        setNewInternalStakeholders(internalStakeholdersObj)
        setInternalStakeholders(internalStakeholdersObj)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsData])

    function updateState(data) {
        setEvidenceCategories(data.evidenceCategories)
    }

    const handleVMCheckboxes = (event) => {
        setNewAudiences({
            ...newAudiences,
            [event.target.name]: event.target.checked,
        })
    }

    const handleVMInternalStakeholdersCheckboxes = (event) => {
        setNewInternalStakeholders({
            ...newInternalStakeholders,
            [event.target.name]: event.target.checked,
        })
    }

    function flattenMarkets() {
        let keyMarkets = []
        for (let phase of store.getState().strategy.keyMarkets) {
            keyMarkets = keyMarkets.concat(phase.phaseKeyMarkets)
        }
        return keyMarkets
    }

    const handleClickOpen = () => {
        if (!canCreate) {
            return
        }
        setOpen(true)
    }

    useImperativeHandle(ref, () => ({
        handleClickOpen: handleClickOpen,
    }))

    const handleClose = () => {
        // Dismissing the dialog (backdrop click / ESC) should discard the form
        // the same way the Cancel button does, so stale values like study
        // number don't linger into the next open.
        handleCancel()
    }

    const handleChange = (event) => {
        setGapPriority(event.target.value)
    }

    async function autoGenerateAISummary(newFile) {
        if (!_.isEmpty(newFile)) {
            setErrorMessage('')
            setIsStepFailed([])
            setDidGenerate(true)
            setFile(newFile)
            setIsAILoading(true)

            timeout(4000).then(() => {
                setStepper((prev) => prev + 1)
                timeout(2000).then(async () => {
                    try {
                        const msg = await processPDFWithAIAsync({ file: newFile.link })
                        setAuthor(msg.authors.join(', '))
                        setEvidenceName(msg.title)
                        setEvidenceAbstract(msg.abstract) // Updated to try and include more details from the abstract
                        setEvidenceYear(msg.year.toString())
                        setEvidenceCategory(msg.category)
                        setPubmedId(msg.PMID)
                        setShortCitation(msg.shortCitation || '')
                        setLongCitation(msg.longCitation || '')
                        setCitation(msg.longCitation || msg.shortCitation || '')
                        setLink(msg.url)
                        setUniqueIdentifier(msg.uniqueIdentifier)
                        setIsAILoading(false)
                        setIsAIGeneratedSummary(true)
                    } catch (error) {
                        let message = error?.response?.data?.message
                        message = message ? message : 'Oops! Something went wrong during upload. Please try again.'
                        setErrorMessage(message)
                        console.error('Error with Processing Document:', error)
                        setIsStepFailed((prev) => [...prev, 2])
                    }

                    setStepper((prev) => prev + 1)
                })
            })

            setStepper(0)
        }
    }

    async function reGenerateAISummary() {
        if (Object.keys(file).length === 0) {
            cancelRegenerateAISummary()
        }
        setIsStepFailed([])
        await autoGenerateAISummary(file)
    }

    function cancelRegenerateAISummary() {
        setDidGenerate(false)
        setIsAILoading(false)
        setIsStepFailed([])
    }

    const getIsFailedStep = (index) => {
        return isStepFailed.includes(index)
    }

    const [isAIGeneratedSummary, setIsAIGeneratedSummary] = useState(false)
    const [isAILoading, setIsAILoading] = useState(false)

    const handleCancel = () => {
        setOpen(false)

        setIsAILoading(false)
        setDidGenerate(false)
        setStatus('Supported')
        setName('')
        setVMDescription('')
        setNewAudiences(audiences)
        setNewInternalStakeholders(internalStakeholders)
        setPriority('---')
        setNewLocalMarkets(flattenMarkets().map((market) => market.code))
        setOpenNewLocalVMDialog(false)

        setEvidenceName('')
        setEvidenceAbstract('')
        setEvidenceYear(2020)
        setEvidenceIsInternal(false)
        setEvidenceCategory('---')
        setFile({})
        setBatchFiles([])
        setBatchResults([])
        setBatchPhase('idle')
        setBatchProcessingIndex(0)
        setDraftStatus('draft')
        setStatusMenuAnchor(null)
        setLocalKES([])
        setLink('')
        setAuthor('')
        setEvidencePopulation('')
        setPubmedId('')
        setCitation('')
        setNickname('')
        setPublicationType('')
        setUniqueIdentifier('')

        setIsAILoading(false)
        setDidGenerate(false)
        setIsAIGeneratedSummary(false)
        setStepper(0)

        setGapNotes('')
        setGapPriority('medium')
        setGapSummary('')
        setGapTitle('')
        setNewLeverValue(leverValue)

        setDeliverableDate(null)
        setDeliverableTitle('')
        setDeliverableIsComplete(false)
        setDeliverableType('')
    }

    const handleLocalMarkets = (event) => {
        if (event.target.checked) {
            if (!newLocalMarkets.includes(event.target.name)) {
                let temp = Array.from(newLocalMarkets)
                temp.push(event.target.name)
                setNewLocalMarkets(temp)
            }
        } else {
            if (newLocalMarkets.includes(event.target.name)) {
                let temp = Array.from(newLocalMarkets)
                let filtered = temp.filter((e) => e !== event.target.name)
                setNewLocalMarkets(filtered)
            }
        }
    }

    const handleSave = async () => {
        switch (props.id) {
            case 'vm':
                if (store.getState().selectedMarket && store.getState().selectedMarket !== 'Global') {
                    setOpen(false)
                    setOpenNewLocalVMDialog(true)
                    break
                }
                let tempVM = {
                    name: name,
                    priority: priority,
                    order: 0,
                    status,
                    strategyId: store.getState().strategy._id,
                    organizationId: store.getState().strategy.organizationId,
                    linkedEvidence: [],
                    linkedActivities: [],
                    description: vmDescription,
                    audiences: newAudiences,
                    internalStakeholders: newInternalStakeholders,
                    primaryEvidence: [],
                    localMarkets:
                        store.getState().selectedMarket && store.getState().selectedMarket !== 'Global'
                            ? [store.getState().selectedMarket]
                            : newLocalMarkets,
                    isCountrySpecific: store.getState().selectedMarket && store.getState().selectedMarket !== 'Global',
                }
                createNew(tempVM)
                logEvent(ACTIONS.CREATE, { type: 'Value Message', data: tempVM })
                setName('')
                setPriority('---')
                setStatus('Supported')
                setNewAudiences(audiences)
                setNewInternalStakeholders(internalStakeholders)
                setVMDescription('')
                setNewLocalMarkets(flattenMarkets().map((market) => market.code))
                break
            case 'evidence':
                if (isSubmittingLocal || props.isLoading) return
                setIsSubmittingLocal(true)
                let timestamp = _.isEmpty(file) ? '' : Date.now().toString()
                let tempEvidence = {
                    name: evidenceName.replace(/\n+/gm, ' '),
                    s3Name: timestamp,
                    link: link,
                    mimeType: !_.isEmpty(file) ? file.link.type : '',
                    originalFileName: !_.isEmpty(file) ? file.link.name : '',
                    abstract: evidenceAbstract,
                    category: evidenceCategory,
                    year: evidenceYear,
                    internal: evidenceIsInternal,
                    author: author,
                    order: 0,
                    sharepointFileLink: '',
                    strategyId: store.getState().strategy._id,
                    organizationId: store.getState().strategy.organizationId,
                    linkedActivities: [],
                    linkedValueMessages: [],
                    primaryValueMessages: [],
                    uniqueIdentifier: uniqueIdentifier,
                    population: evidencePopulation,
                    publicationType: publicationType,
                    nickname: nickname,
                    citation: longCitation || citation,
                    shortCitation: shortCitation,
                    longCitation: longCitation,
                    pubmedId: pubmedId,
                    maiaProcessing: {
                        status: 'unavailable',
                        processedAt: new Date(),
                        error: '',
                    },
                    status: draftStatus,
                }
                // Handles file upload if there's an attached file

                let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

                if (!_.isEmpty(file)) {
                    await uploadFile(file, timestamp, 'evidence', token)
                }
                try {
                    const newDraft = await createNew(tempEvidence)
                    if (newDraft?.id) {
                        await saveLocalKES(newDraft.id, token)
                        if (!_.isEmpty(file) && file.link?.name?.endsWith('.pdf')) {
                            processEvidenceForMaia({ evidenceId: newDraft.id })
                        }
                    }
                } finally {
                    setIsSubmittingLocal(false)
                }

                logEvent(ACTIONS.CREATE, { type: 'Evidence', data: tempEvidence })
                setIsAILoading(false)
                setDidGenerate(false)
                setEvidenceName('')
                setEvidenceAbstract('')
                setEvidenceYear(2020)
                setEvidenceCategory('---')
                setEvidenceIsInternal(false)
                setFile({})
                setLink('')
                setAuthor('')
                setCitation('')
                setEvidencePopulation('')
                setNickname('')
                setUniqueIdentifier('')
                setPubmedId('')
                setPublicationType('')
                break
            case 'gap':
                createNew({
                    title: gapTitle,
                    priority: gapPriority,
                    notes: gapNotes,
                    summary: gapSummary,
                    leverValue: newLeverValue,
                })
                setGapNotes('')
                setGapPriority('')
                setGapSummary('')
                setGapTitle('')
                setLeverValue(newLeverValue)
                break
            case 'deliverables':
                createNew({
                    title: deliverableTitle,
                    type: deliverableType,
                    isComplete: deliverableIsComplete,
                    dueDate: deliverableDate,
                    organizationId: store.getState().strategy.organizationId,
                    strategyId: store.getState().strategy._id,
                })
                setDeliverableDate('')
                setDeliverableTitle('')
                setDeliverableIsComplete(false)
                setDeliverableType('')
                break
            case 'tracelink':
                createNew({
                    slug: tracelinkSlug,
                    owner: tracelinkOwner,
                })
                break
            default:
                break
        }
        setOpen(false)
    }

    const dialogueHeaderMapping = {
        vm: {
            header: valueMessageDisplayName.singular,
            description: `Enter your new ${valueMessageDisplayName.singular} below`,
            placeholder: `New ${valueMessageDisplayName.singular}`,
        },
        activity: {
            header: activitiesDisplayName,
            description: `Enter your new ${activityDisplayName} below`,
            placeholder: `New ${activityDisplayName}`,
        },
        strategicimperative: {
            header: strategicImperativeDisplayName.plural,
            description: `Enter your new ${strategicImperativeDisplayName.singular} below`,
            placeholder: `New ${strategicImperativeDisplayName.singular}`,
        },
        evidence: {
            header: evidenceDisplayName,
            description: `Enter your new ${evidenceDisplayName} below`,
            placeholder: `New ${evidenceDisplayName}`,
        },
        gap: {
            header: 'Market Access Goals',
            description: 'Enter a new Market Access Goal',
            placeholder: 'New Gap Analysis',
        },
        deliverables: {
            header: 'Deliverables',
            description: 'Add a new Deliverable',
            placeholder: 'New Deliverable',
        },
        tracelink: {
            header: 'Tracelinks',
            description: 'Add a new Tracelink',
            placeholder: 'New Tracelink',
        },
    }

    function regenerateAI() {
        console.log('regen')
        autoGenerateAISummary(file)
    }

    // Routes Dropzone output: any files → batch mode so KES generation fires for all uploads
    function handleDropzoneFiles(acceptedFiles) {
        if (acceptedFiles.length >= 1) {
            startBatchProcessing(acceptedFiles)
        }
    }

    function startBatchProcessing(files) {
        setBatchFiles(files)
        setBatchResults([])
        setBatchProcessingIndex(0)
        setBatchPhase('processing')
        processBatchFiles(files)
    }

    async function processBatchFiles(files) {
        const results = []
        for (let i = 0; i < files.length; i++) {
            setBatchProcessingIndex(i)
            const f = files[i]
            setErrorMessage('')
            setIsStepFailed([])
            setDidGenerate(true)
            setIsAILoading(true)
            setStepper(0)
            await new Promise((resolve) => {
                timeout(4000).then(() => {
                    setStepper(1)
                    timeout(2000).then(async () => {
                        let aiData = {}
                        try {
                            aiData = await processPDFWithAIAsync({ file: f })
                        } catch (err) {
                            console.error(`[Batch] AI failed for ${f.name}:`, err)
                        }
                        setStepper(2)

                        // Save draft to DB immediately so KES generation fires for all file types
                        let draftId = null
                        try {
                            draftId = await uploadFileAndCreateDraftAsync({ file: f, aiData })
                        } catch (err) {
                            console.error(`[Batch] Draft creation failed for ${f.name}:`, err)
                        }

                        results.push({ file: f, ai: aiData, error: null, editedValues: null, draftId })
                        setIsAILoading(false)
                        resolve()
                    })
                })
            })
        }
        setBatchResults(results)
        setBatchPhase('idle')
        setOpen(false)
        setBatchReviewOpen(true)
    }

    function handleDeleteFile() {
        setFile({})
        setEvidenceName('')
        setEvidenceAbstract('')
        setEvidenceYear(2020)
        setEvidenceIsInternal(false)
        setEvidenceCategory('---')
        setFile({})
        setLink('')
        setAuthor('')
        setIsAILoading(false)
        setDidGenerate(false)
        setIsAIGeneratedSummary(false)
        setStepper(0)
    }

    return (
        <Grid container item xs>
            {props.id !== 'evidenceGaps' && canCreate && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClickOpen}
                    disabled={!!props.disabled}
                    style={{ width: 140, height: 52, borderRadius: '14px' }}
                >
                    Add New
                </Button>
            )}

            <Dialog open={open} onClose={handleClose} fullWidth aria-labelledby="form-dialog-title" maxWidth="lg">
                {props.id !== 'evidenceGaps' && (
                    <>
                        <DialogTitle id="form-dialog-title">
                            <Typography variant="h3">{dialogueHeaderMapping[props.id].header}</Typography>
                        </DialogTitle>
                    </>
                )}
                <DialogContent dividers>
                    {(() => {
                        switch (props.id) {
                            case 'vm':
                                return (
                                    <Grid container direction="column" spacing={1}>
                                        <Grid item xs>
                                            <TextField
                                                margin="dense"
                                                id="name"
                                                label={dialogueHeaderMapping[props.id].placeholder}
                                                type="text"
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={name}
                                                onFocus={(event) => {
                                                    event.target.setAttribute('autocomplete', 'off')
                                                }}
                                                onChange={(e) => setName(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <TextField
                                                margin="dense"
                                                id="vmDescription"
                                                label="Description"
                                                type="text"
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={vmDescription}
                                                onFocus={(event) => {
                                                    event.target.setAttribute('autocomplete', 'off')
                                                }}
                                                onChange={(e) => setVMDescription(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid container item fullWidth spacing={2} sx={{ mt: 0.5 }}>
                                            <Grid item xs>
                                                <FormControl fullWidth>
                                                    <InputLabel htmlFor="outlined-age-native-simple-priority">
                                                        Priority
                                                    </InputLabel>
                                                    <Select
                                                        value={priority}
                                                        label="Priority"
                                                        inputProps={{
                                                            name: 'age',
                                                            id: 'outlined-age-native-simple',
                                                        }}
                                                        onChange={(e) => setPriority(e.target.value)}
                                                    >
                                                        <MenuItem value={'---'}>---</MenuItem>
                                                        <MenuItem value={'Low'}>Low</MenuItem>
                                                        <MenuItem value={'Medium'}>Medium</MenuItem>
                                                        <MenuItem value={'High'}>High</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid container item fullWidth spacing={2} mt={0.5} mb={2}>
                                            <Grid item xs>
                                                <FormControl fullWidth>
                                                    <InputLabel htmlFor="status">Status</InputLabel>
                                                    <Select
                                                        value={status}
                                                        label="Status"
                                                        inputProps={{
                                                            name: 'status',
                                                            id: 'status',
                                                        }}
                                                        onChange={(e) => setStatus(e.target.value)}
                                                    >
                                                        <MenuItem value={'Supported'}>Supported</MenuItem>
                                                        <MenuItem value={'Supported-suboptimal'}>
                                                            Supported-suboptimal
                                                        </MenuItem>
                                                        <MenuItem value={'Aspirational'}>Aspirational</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid container item spacing={2} justifyItems="space-evenly">
                                            <Grid item xs>
                                                <Typography variant="subtitle2">Audience:</Typography>
                                                <FormGroup>
                                                    {Object.keys(newAudiences).map((audience, i) => (
                                                        <FormControlLabel
                                                            key={audience + i}
                                                            control={
                                                                <Checkbox
                                                                    checked={newAudiences[audience]}
                                                                    onChange={handleVMCheckboxes}
                                                                    name={audience}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={audience}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </Grid>
                                            <Grid container item spacing={2} justifyItems="space-evenly">
                                                <Grid item xs>
                                                    <Typography variant="subtitle2">Internal Stakeholders:</Typography>
                                                    <FormGroup>
                                                        {Object.keys(newInternalStakeholders).map(
                                                            (internalStakeholder, i) => (
                                                                <FormControlLabel
                                                                    key={internalStakeholder + i}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={
                                                                                newInternalStakeholders[
                                                                                    internalStakeholder
                                                                                ]
                                                                            }
                                                                            onChange={
                                                                                handleVMInternalStakeholdersCheckboxes
                                                                            }
                                                                            name={internalStakeholder}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label={internalStakeholder}
                                                                />
                                                            )
                                                        )}
                                                    </FormGroup>
                                                </Grid>
                                            </Grid>
                                            {settings &&
                                                settings.enableLocalizedMarkets &&
                                                store.getState().selectedMarket === 'Global' && (
                                                    <Grid item xs>
                                                        <Typography variant="subtitle2">Markets:</Typography>
                                                        <FormGroup>
                                                            {flattenMarkets().map((market, i) => (
                                                                <Grid
                                                                    container
                                                                    spacing={1}
                                                                    key={market + i}
                                                                    alignItems="center"
                                                                >
                                                                    <Grid item>
                                                                        <ReactCountryFlag
                                                                            countryCode={market.code?.toLowerCase()}
                                                                            svg
                                                                            cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                                                            cdnSuffix="svg"
                                                                            style={{
                                                                                width: '1.7em',
                                                                                height: '1.7em',
                                                                                margin: '2px',
                                                                            }}
                                                                            title={market.label}
                                                                            className="countryFlag"
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <WhiteOutlineCheckbox
                                                                                    checked={newLocalMarkets.includes(
                                                                                        market.code
                                                                                    )}
                                                                                    onChange={handleLocalMarkets}
                                                                                    name={market.code}
                                                                                    color="primary"
                                                                                />
                                                                            }
                                                                            label={market.label}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            ))}
                                                        </FormGroup>
                                                    </Grid>
                                                )}
                                        </Grid>
                                    </Grid>
                                )
                            case 'activity':
                                return (
                                    <ActivityForm
                                        mode={'Create'}
                                        onSubmit={async (data) => {
                                            try {
                                                const activityId = await createActivityMutation({ data })
                                                logEvent(ACTIONS.CREATE, { type: 'Activity', data })
                                                await autoLinkActivityToExternalSource({
                                                    activityId,
                                                    roadmapId: data.strategyId,
                                                    studyNumber: data.studyNumber,
                                                    source: data.source,
                                                })
                                                invalidateActivitiesQuery()
                                                invalidateGetValueMessageLinkedDataQuery()
                                                setActivitySaveError(null)
                                                setOpen(false)
                                            } catch (err) {
                                                setActivitySaveError(err.message ?? 'Failed to create activity')
                                            }
                                        }}
                                        onCancel={() => {
                                            setActivitySaveError(null)
                                            setOpen(false)
                                        }}
                                    />
                                )
                            case 'strategicimperative':
                                return (
                                    <StrategicImperativeForm
                                        mode="Create"
                                        onSubmit={async (fields) => {
                                            await createStrategicImperative(fields)
                                            setOpen(false)
                                        }}
                                        onCancel={handleCancel}
                                    />
                                )
                            case 'evidence':
                                if (isStepFailed.length > 0) {
                                    return (
                                        <>
                                            <Grid container item xs={12}>
                                                <Box sx={{ width: '100%', marginTop: '20px' }}>
                                                    <Stepper
                                                        activeStep={stepper}
                                                        alternativeLabel
                                                        sx={{ width: '100%' }}
                                                    >
                                                        {steps.map((label, index) => (
                                                            <Step key={label}>
                                                                <ColorlibStepLabel error={getIsFailedStep(index)}>
                                                                    {label}
                                                                </ColorlibStepLabel>
                                                            </Step>
                                                        ))}
                                                    </Stepper>
                                                </Box>
                                            </Grid>
                                            {/* Server-side error messages display in this Alert*/}
                                            {/* Filetype, # of files, generic messages display in Dropzone component*/}
                                            <Grid container item xs={12}>
                                                <Box sx={{ width: '100%', marginTop: '10px' }}>
                                                    <Alert severity="error" message={errorMessage} />
                                                </Box>
                                            </Grid>
                                            {errorMessage && (
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        <Dropzone
                                                            setFile={setFile}
                                                            generate={handleDropzoneFiles}
                                                            acceptedFileTypes={acceptedFileTypes}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )}
                                            <DialogActions>
                                                <Button
                                                    onClick={cancelRegenerateAISummary}
                                                    variant="text"
                                                    color="primary"
                                                >
                                                    Cancel
                                                </Button>
                                                <Button onClick={reGenerateAISummary} variant="text" color="primary">
                                                    Retry
                                                </Button>
                                            </DialogActions>
                                        </>
                                    )
                                }

                                return (
                                    <Grid container fullWidth direction="column" spacing={1}>
                                        {/*-----------------AI AUTO-IMPORT ENABLED/DISABLED TOGGLE ---------------------------*/}
                                        {isAIEnabled && !isAILoading && !isAIGeneratedSummary && (
                                            <Grid>
                                                {/*<FormControl>*/}
                                                {/*    <FormControlLabel*/}
                                                {/*        control={*/}
                                                {/*            <Switch*/}
                                                {/*                checked={isAIAutoImportEnabled}*/}
                                                {/*                onChange={(e) => {*/}
                                                {/*                    setIsAIAutoImportEnabled(!isAIAutoImportEnabled)*/}
                                                {/*                }}*/}
                                                {/*                name='checkedAI'*/}
                                                {/*            />*/}
                                                {/*        }*/}
                                                {/*        label={`Auto-Extract Data With ATLAS AI ${isAIAutoImportEnabled ? 'Enabled' : 'Disabled'}`}*/}
                                                {/*    />*/}
                                                {/*</FormControl>*/}
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    width="100%"
                                                >
                                                    <h4 color="#0049EA">{`Import PDF Data with ATLAS AI`}</h4>
                                                    <PubMedView
                                                        trigger={(handleOpen) => (
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                size="large"
                                                                onClick={handleOpen}
                                                            >
                                                                Import from PubMed
                                                            </Button>
                                                        )}
                                                        onImported={props.onEvidenceImported}
                                                        initialDiseaseArea={store.getState().strategy.indication || ''}
                                                        initialInterventions={store.getState().strategy.product || ''}
                                                    />
                                                </Box>
                                            </Grid>
                                        )}
                                        {/*-----------------REGENERATE  ---------------------------*/}
                                        {didGenerate && !isAILoading && (
                                            <Box p={1} display="flex" alignItems="center" gap={3}>
                                                <AtlasAIButton
                                                    onClick={regenerateAI}
                                                    isLoading={isAILoading}
                                                    isGenerated
                                                />
                                                <Chip
                                                    icon={<Logo />}
                                                    label={!_.isEmpty(file) ? file.link.name : 'File'}
                                                    variant="outlined"
                                                    onDelete={handleDeleteFile}
                                                />
                                            </Box>
                                        )}
                                        <Grid>
                                            {/*/!*----------------- FIRST TIME - NO FILE ---------------------------*!/*/}
                                            {isAIEnabled && !isAILoading && !didGenerate && batchPhase === 'idle' && (
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        <Dropzone
                                                            setFile={setFile}
                                                            generate={handleDropzoneFiles}
                                                            acceptedFileTypes={acceptedFileTypes}
                                                            maxFiles={5}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )}
                                            {/*----------------- GENERATING  ---------------------------*/}
                                            {isAILoading && (
                                                <>
                                                    {batchPhase === 'processing' && (
                                                        <Typography
                                                            variant="body2"
                                                            align="center"
                                                            sx={{ mt: 1, color: 'text.secondary' }}
                                                        >
                                                            Processing file {batchProcessingIndex + 1} of{' '}
                                                            {batchFiles.length}:{' '}
                                                            {batchFiles[batchProcessingIndex]?.name}
                                                        </Typography>
                                                    )}
                                                    <Grid container item xs={12}>
                                                        <Box sx={{ width: '100%', marginTop: '20px' }}>
                                                            <Stepper activeStep={stepper} alternativeLabel>
                                                                {steps.map((label, index) => (
                                                                    <Step key={label}>
                                                                        <ColorlibStepLabel
                                                                            error={getIsFailedStep(index)}
                                                                        >
                                                                            {label}
                                                                        </ColorlibStepLabel>
                                                                    </Step>
                                                                ))}
                                                            </Stepper>
                                                        </Box>
                                                    </Grid>
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            left: '50%',
                                                            top: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            borderRadius: '100%',
                                                            zIndex: 1,
                                                            backgroundColor: 'background.default',
                                                        }}
                                                    >
                                                        <AtlasAIButton size={100} isLoading />
                                                    </Box>
                                                </>
                                            )}
                                        </Grid>
                                        {/*----------------- DATA FIELDS PRE/POST GENERATE  ---------------------------*/}
                                        <>
                                            {!isAILoading && didGenerate && (pubmedId || !_.isEmpty(file)) && (
                                                <Grid item>
                                                    <Divider sx={{ mb: '24px' }} />
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start',
                                                            gap: '24px',
                                                            alignSelf: 'stretch',
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                                                            <Typography
                                                                variant="18x500"
                                                                color="figma.grayscale.textHier1"
                                                            >
                                                                Source:
                                                            </Typography>
                                                            <Typography
                                                                variant="18x400"
                                                                color="figma.grayscale.textHier2"
                                                            >
                                                                {pubmedId ? 'PubMed' : 'File Upload'}
                                                            </Typography>
                                                        </Box>
                                                        {!_.isEmpty(file) && file?.link?.name && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="18x500"
                                                                    color="figma.grayscale.textHier1"
                                                                >
                                                                    {file.link.name}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        padding: '4px 12px',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        borderRadius: '30px',
                                                                        bgcolor:
                                                                            DRAFT_STATUS_BADGE_COLOR[draftStatus] ??
                                                                            'figma.grayscale.tier4',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="14x500"
                                                                        color="figma.grayscale.contentWrapper"
                                                                    >
                                                                        {DRAFT_STATUS_DISPLAY[draftStatus] ??
                                                                            draftStatus}
                                                                    </Typography>
                                                                </Box>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) =>
                                                                        setStatusMenuAnchor(e.currentTarget)
                                                                    }
                                                                >
                                                                    <ActionsIcon style={{ width: 20, height: 20 }} />
                                                                </IconButton>
                                                                <Popover
                                                                    open={Boolean(statusMenuAnchor)}
                                                                    anchorEl={statusMenuAnchor}
                                                                    onClose={() => setStatusMenuAnchor(null)}
                                                                    anchorOrigin={{
                                                                        vertical: 'bottom',
                                                                        horizontal: 'right',
                                                                    }}
                                                                    transformOrigin={{
                                                                        vertical: 'top',
                                                                        horizontal: 'right',
                                                                    }}
                                                                    PaperProps={{
                                                                        sx: {
                                                                            display: 'inline-flex',
                                                                            padding: '12px',
                                                                            flexDirection: 'column',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'flex-start',
                                                                            gap: '10px',
                                                                            borderRadius: '12px',
                                                                            bgcolor: 'figma.grayscale.tier7',
                                                                            boxShadow: '0 4px 8px 0 rgba(0,0,0,0.20)',
                                                                        },
                                                                    }}
                                                                >
                                                                    {DRAFT_STATUS_ACTIONS.map(({ label, value }) => (
                                                                        <Box
                                                                            key={value}
                                                                            onClick={() => {
                                                                                setDraftStatus(value)
                                                                                setStatusMenuAnchor(null)
                                                                            }}
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                padding: '4px 8px',
                                                                                borderRadius: '6px',
                                                                                width: '100%',
                                                                                '&:hover': {
                                                                                    bgcolor: 'figma.grayscale.tier6',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="14x400"
                                                                                color="figma.grayscale.textHier2"
                                                                            >
                                                                                {label}
                                                                            </Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Popover>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Divider sx={{ mt: '24px' }} />
                                                </Grid>
                                            )}
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <FormControl>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={evidenceIsInternal}
                                                                    onChange={(e) =>
                                                                        setEvidenceIsInternal(e.target.checked)
                                                                    }
                                                                    name="Internal/External"
                                                                />
                                                            }
                                                            label="External"
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        variant={'rectangular'}
                                                        width={50}
                                                        animation={'wave'}
                                                    />
                                                </Grid>
                                            )}
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <TextField
                                                        margin="dense"
                                                        id="evidence-name"
                                                        label={`${evidenceDisplayName} Title`}
                                                        type="text"
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        value={evidenceName}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off')
                                                        }}
                                                        // InputLabelProps={{
                                                        //     shrink: true,
                                                        // }}
                                                        onChange={(e) => setEvidenceName(e.target.value)}
                                                    />
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        variant={'rectangular'}
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        width={'100%'}
                                                        height={118}
                                                        animation={'wave'}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid container item spacing={1}>
                                                <Grid item xs>
                                                    {!isAILoading ? (
                                                        <TextField
                                                            id="usi"
                                                            margin="dense"
                                                            label="Unique Study Identifier"
                                                            type="text"
                                                            fullWidth
                                                            value={uniqueIdentifier}
                                                            onFocus={(event) => {
                                                                event.target.setAttribute('autocomplete', 'off')
                                                            }}
                                                            onChange={(e) => setUniqueIdentifier(e.target.value)}
                                                        />
                                                    ) : (
                                                        <Skeleton
                                                            variant={'rectangular'}
                                                            sx={{ bgcolor: SKELETON_COLOR }}
                                                            animation={'wave'}
                                                            width={'100%'}
                                                            height={45}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs>
                                                    {!isAILoading ? (
                                                        <TextField
                                                            id="pmid"
                                                            margin="dense"
                                                            label="PubMed ID"
                                                            type="text"
                                                            fullWidth
                                                            value={pubmedId}
                                                            onFocus={(event) => {
                                                                event.target.setAttribute('autocomplete', 'off')
                                                            }}
                                                            onChange={(e) => setPubmedId(e.target.value)}
                                                        />
                                                    ) : (
                                                        <Skeleton
                                                            variant={'rectangular'}
                                                            sx={{ bgcolor: SKELETON_COLOR }}
                                                            animation={'wave'}
                                                            width={'100%'}
                                                            height={45}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <TextField
                                                        margin="dense"
                                                        label="Nickname"
                                                        type="text"
                                                        fullWidth
                                                        value={nickname}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off')
                                                        }}
                                                        onChange={(e) => setNickname(e.target.value)}
                                                    />
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        variant={'rectangular'}
                                                        animation={'wave'}
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        width={'100%'}
                                                        height={45}
                                                    />
                                                </Grid>
                                            )}
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <TextField
                                                        margin="dense"
                                                        label="Key Points"
                                                        helperText={
                                                            isAIGeneratedSummary
                                                                ? '*Generated with ATLAS AI - please verify accuracy.'
                                                                : ''
                                                        }
                                                        type="text"
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        value={evidenceAbstract}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off')
                                                        }}
                                                        onChange={(e) => setEvidenceAbstract(e.target.value)}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                resize: 'vertical',
                                                                overflow: 'hidden',
                                                                minHeight: '120px',
                                                            },
                                                            '& textarea': {
                                                                resize: 'none',
                                                                overflow: 'auto !important',
                                                                height: '100% !important',
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        variant={'rectangular'}
                                                        animation={'wave'}
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        width={'100%'}
                                                        height={118}
                                                    />
                                                </Grid>
                                            )}
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <TextField
                                                        margin="dense"
                                                        label="Short Citation"
                                                        type="text"
                                                        fullWidth
                                                        value={shortCitation}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off')
                                                        }}
                                                        onChange={(e) => setShortCitation(e.target.value)}
                                                    />
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        variant={'rectangular'}
                                                        animation={'wave'}
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        width={'100%'}
                                                        height={45}
                                                    />
                                                </Grid>
                                            )}
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <TextField
                                                        margin="dense"
                                                        label="Long Citation"
                                                        type="text"
                                                        fullWidth
                                                        multiline
                                                        rows={2}
                                                        value={longCitation}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off')
                                                        }}
                                                        onChange={(e) => setLongCitation(e.target.value)}
                                                    />
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        variant={'rectangular'}
                                                        animation={'wave'}
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        width={'100%'}
                                                        height={72}
                                                    />
                                                </Grid>
                                            )}
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <TextField
                                                        margin="dense"
                                                        id="evidence-author"
                                                        label="Author(s)"
                                                        type="text"
                                                        fullWidth
                                                        value={author}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off')
                                                        }}
                                                        onChange={(e) => setAuthor(e.target.value)}
                                                    />
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        variant={'rectangular'}
                                                        animation={'wave'}
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        width={'100%'}
                                                        height={45}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid container item spacing={1}>
                                                <Grid item xs>
                                                    {!isAILoading ? (
                                                        <FormControl fullWidth margin="dense">
                                                            <InputLabel htmlFor="outlined-age-native-evidence-pop">
                                                                Population
                                                            </InputLabel>
                                                            <Select
                                                                label="POPULATION"
                                                                inputProps={{
                                                                    id: `outlined-age-native-simple-pop`,
                                                                }}
                                                                value={evidencePopulation}
                                                                onChange={(e) => setEvidencePopulation(e.target.value)}
                                                            >
                                                                <MenuItem value={'---'}>---</MenuItem>
                                                                {store
                                                                    .getState()
                                                                    .settings.vmSubpopulations.map((category, i) => (
                                                                        <MenuItem value={category} key={category + i}>
                                                                            {category}
                                                                        </MenuItem>
                                                                    ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Skeleton
                                                            variant={'rectangular'}
                                                            sx={{ bgcolor: SKELETON_COLOR }}
                                                            animation={'wave'}
                                                            width={'100%'}
                                                            height={45}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs>
                                                    {!isAILoading ? (
                                                        <FormControl fullWidth margin="dense">
                                                            <InputLabel htmlFor="outlined-age-native-evidence-pub-type">
                                                                Publication Type
                                                            </InputLabel>
                                                            <Select
                                                                label="PUBLICATION TYPE"
                                                                inputProps={{
                                                                    id: `outlined-age-native-simple-pub-type`,
                                                                }}
                                                                value={publicationType}
                                                                onChange={(e) => setPublicationType(e.target.value)}
                                                            >
                                                                <MenuItem value={'---'}>---</MenuItem>
                                                                {Object.values(PUBLICATIONS).map((category, i) => (
                                                                    <MenuItem value={category} key={category + i}>
                                                                        {category}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Skeleton
                                                            variant={'rectangular'}
                                                            sx={{ bgcolor: SKELETON_COLOR }}
                                                            animation={'wave'}
                                                            width={'100%'}
                                                            height={45}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>

                                            <Grid container item spacing={1}>
                                                <Grid item xs>
                                                    {!isAILoading ? (
                                                        <TextField
                                                            id="year"
                                                            margin="dense"
                                                            label="Year"
                                                            type="number"
                                                            fullWidth
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                            value={evidenceYear}
                                                            onFocus={(event) => {
                                                                event.target.setAttribute('autocomplete', 'off')
                                                            }}
                                                            onChange={(e) =>
                                                                setEvidenceYear(Number.parseInt(e.target.value))
                                                            }
                                                        />
                                                    ) : (
                                                        <Skeleton
                                                            variant={'rectangular'}
                                                            sx={{ bgcolor: SKELETON_COLOR }}
                                                            animation={'wave'}
                                                            width={'100%'}
                                                            height={45}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs>
                                                    {!isAILoading ? (
                                                        <FormControl fullWidth margin="dense">
                                                            <InputLabel htmlFor="outlined-age-native-evidence-category">
                                                                Category
                                                            </InputLabel>
                                                            <Select
                                                                label="CATEGORY"
                                                                inputProps={{
                                                                    id: `outlined-age-native-simple-${evidenceCategory}`,
                                                                }}
                                                                value={evidenceCategory}
                                                                onChange={(e) => setEvidenceCategory(e.target.value)}
                                                            >
                                                                <MenuItem value={'---'}>---</MenuItem>
                                                                {evidenceCategories.map((category, i) => (
                                                                    <MenuItem value={category} key={category + i}>
                                                                        {category}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Skeleton
                                                            variant={'rectangular'}
                                                            sx={{ bgcolor: SKELETON_COLOR }}
                                                            animation={'wave'}
                                                            width={'100%'}
                                                            height={45}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                            {!isAILoading ? (
                                                <Grid item>
                                                    <TextField
                                                        margin="dense"
                                                        label="URL"
                                                        type="text"
                                                        fullWidth
                                                        value={link}
                                                        placeholder={'http://'}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off')
                                                        }}
                                                        onChange={(e) => setLink(e.target.value)}
                                                    />
                                                </Grid>
                                            ) : (
                                                <Grid item>
                                                    <Skeleton
                                                        variant={'rectangular'}
                                                        sx={{ bgcolor: SKELETON_COLOR }}
                                                        animation={'wave'}
                                                        width={'100%'}
                                                        height={45}
                                                    />
                                                </Grid>
                                            )}
                                            {/* KES entry — hidden during AI loading */}
                                            {!isAILoading && (
                                                <>
                                                    <Grid item>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                cursor: 'pointer',
                                                                mt: 1,
                                                            }}
                                                            onClick={handleAddKES}
                                                        >
                                                            <AddCircleIcon style={{ width: 20, height: 20 }} />
                                                            <Typography
                                                                variant="16x400"
                                                                color="figma.grayscale.textHier1"
                                                            >
                                                                Add Key Evidence Statements
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    {localKES.map((kes, index) => (
                                                        <Grid item key={kes.id}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    padding: '16px',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                    gap: '10px',
                                                                    alignSelf: 'stretch',
                                                                    borderRadius: '12px',
                                                                    border: '1px solid',
                                                                    borderColor: 'figma.grayscale.stroke',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'flex-start',
                                                                        gap: 1,
                                                                        width: '100%',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="22x300"
                                                                        color="figma.grayscale.textHier2"
                                                                        sx={{
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {index + 1}.
                                                                    </Typography>
                                                                    <TextField
                                                                        variant="standard"
                                                                        placeholder="Enter here"
                                                                        value={kes.statement}
                                                                        onChange={(e) =>
                                                                            handleKESTextChange(kes.id, e.target.value)
                                                                        }
                                                                        multiline
                                                                        fullWidth
                                                                        InputProps={{ disableUnderline: true }}
                                                                        sx={{
                                                                            flex: '1 0 0',
                                                                            '& .MuiInputBase-input': {
                                                                                color: 'figma.grayscale.textHier2',
                                                                            },
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            <IconButton
                                                                component="label"
                                                                sx={{ mt: 0.5, borderRadius: 1 }}
                                                            >
                                                                <input
                                                                    type="file"
                                                                    multiple
                                                                    hidden
                                                                    accept=".png,.jpg,.jpeg"
                                                                    onChange={(e) => handleKESFileSelect(e, kes.id)}
                                                                />
                                                                <Typography
                                                                    variant="16x600"
                                                                    color="primary.main"
                                                                    sx={{ textTransform: 'uppercase' }}
                                                                >
                                                                    Attach Figure
                                                                    {kes.figures.length > 0 &&
                                                                        ` (${kes.figures.length})`}
                                                                </Typography>
                                                            </IconButton>
                                                        </Grid>
                                                    ))}
                                                </>
                                            )}
                                        </>
                                    </Grid>
                                )
                            case 'gap':
                                return (
                                    <Grid container>
                                        <DialogContentText>
                                            {dialogueHeaderMapping[props.id].description}
                                        </DialogContentText>
                                        <TextField
                                            margin="dense"
                                            id="gapTitle"
                                            label="Title"
                                            type="text"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            value={gapTitle}
                                            onFocus={(event) => {
                                                event.target.setAttribute('autocomplete', 'off')
                                            }}
                                            onChange={(e) => setGapTitle(e.target.value)}
                                        />
                                        <FormControl component="fieldset">
                                            <FormLabel component="legend">QUALITY RATING</FormLabel>
                                            <RadioGroup
                                                row
                                                aria-label="quality"
                                                name="quality1"
                                                value={gapPriority}
                                                onChange={handleChange}
                                            >
                                                <FormControlLabel value="low" control={<RedRadio />} label="Low" />
                                                <FormControlLabel
                                                    value="medium"
                                                    control={<YellowRadio />}
                                                    label="Medium"
                                                />
                                                <FormControlLabel value="high" control={<GreenRadio />} label="High" />
                                            </RadioGroup>
                                        </FormControl>
                                        <TextField
                                            margin="dense"
                                            id="gapSummary"
                                            label="Summary"
                                            type="text"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            value={gapSummary}
                                            onFocus={(event) => {
                                                event.target.setAttribute('autocomplete', 'off')
                                            }}
                                            onChange={(e) => setGapSummary(e.target.value)}
                                        />
                                    </Grid>
                                )
                            case 'deliverables':
                                return (
                                    <Grid container direction="column" spacing={1}>
                                        <TextField
                                            id="deliverableTitle"
                                            value={deliverableTitle}
                                            fullWidth
                                            margin="normal"
                                            label="Title"
                                            onChange={(e) => setDeliverableTitle(e.target.value)}
                                        />
                                        <FormControl fullWidth margin="dense">
                                            <InputLabel id="pub-type-label">Type</InputLabel>
                                            <Select
                                                labelId="pub-type"
                                                id="pub-type"
                                                value={deliverableType}
                                                inputProps={{
                                                    className: classes.inputLabel,
                                                }}
                                                onChange={(e) => setDeliverableType(e.target.value)}
                                                label="Type"
                                            >
                                                <MenuItem value="None">
                                                    <em>None</em>
                                                </MenuItem>
                                                {categories.map((x, i) => (
                                                    <MenuItem value={x} key={x + i}>
                                                        {x}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Grid container item justify="space-between" wrap="nowrap">
                                            <Grid container item wrap="nowrap" direction="column">
                                                <Grid item>
                                                    <Typography variant="subtitle2">STATUS: </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <FormControlLabel
                                                        control={
                                                            <WhiteOutlineCheckbox
                                                                checked={deliverableIsComplete}
                                                                onChange={(e) =>
                                                                    setDeliverableIsComplete(e.target.checked)
                                                                }
                                                                name="Completed"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Completed"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )
                            case 'tracelink':
                                return (
                                    <Grid container direction="column">
                                        <DialogContentText>
                                            {dialogueHeaderMapping[props.id].description}
                                        </DialogContentText>
                                        <Grid item xs>
                                            <TextField
                                                margin="dense"
                                                id="tracelink-slug"
                                                label="Slug"
                                                type="text"
                                                fullWidth
                                                value={tracelinkSlug}
                                                onFocus={(event) => {
                                                    event.target.setAttribute('autocomplete', 'off')
                                                }}
                                                onChange={(e) => setTracelinkSlug(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <TextField
                                                margin="dense"
                                                id="tracelink-owner"
                                                label="Owner"
                                                type="text"
                                                fullWidth
                                                value={tracelinkOwner}
                                                onFocus={(event) => {
                                                    event.target.setAttribute('autocomplete', 'off')
                                                }}
                                                onChange={(e) => setTracelinkOwner(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                )
                            default:
                                return <p></p>
                        }
                    })()}
                </DialogContent>
                {!isStepFailed.length > 0 && !isAILoading && !FORMS_WITH_OWN_ACTIONS.includes(props.id) && (
                    <DialogActions>
                        <Button onClick={handleCancel} variant="outlined" color="primary">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isSubmittingExtInt}
                            variant="contained"
                            color="primary"
                            sx={{
                                opacity: isSubmittingExtInt ? 0.6 : 1,
                                pointerEvents: isSubmittingExtInt ? 'none' : 'auto',
                            }}
                        >
                            {isSubmittingExtInt ? 'Processing…' : 'Save'}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
            <Snackbar
                open={!!activitySaveError}
                autoHideDuration={6000}
                onClose={() => setActivitySaveError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert onClose={() => setActivitySaveError(null)} severity="error" sx={{ width: '100%' }}>
                    {activitySaveError}
                </MuiAlert>
            </Snackbar>
            <BatchDraftReviewDialog
                open={batchReviewOpen}
                onClose={() => {
                    setBatchReviewOpen(false)
                    setBatchResults([])
                }}
                initialResults={batchResults}
                onSaved={props.onEvidenceImported}
            />
            <Dialog
                open={openNewLocalVMDialog}
                onClose={() => setOpenNewLocalVMDialog(false)}
                aria-labelledby="form-dialog-title1"
            >
                <DialogTitle id="form-dialog-title">Creating new Local {valueMessageDisplayName.singular}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Typography variant="subtitle1">
                            This message will not appear in the Global view, and changes made will remain specific to
                            this. Do you wish to continue?
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} variant="outlined" color="primary">
                        Cancel
                    </Button>
                    <Button
                        disabled={!!props.isLoading}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            createNew({
                                name: name,
                                priority: priority,
                                order: 1,
                                status,
                                strategyId: store.getState().strategy._id,
                                organizationId: store.getState().strategy.organizationId,
                                linkedEvidence: [],
                                linkedActivities: [],
                                description: vmDescription,
                                audiences: newAudiences,
                                internalStakeholders: newInternalStakeholders,
                                primaryEvidence: [],
                                localMarkets: [store.getState().selectedMarket],
                                isCountrySpecific: true,
                            })
                            setOpenNewLocalVMDialog(false)
                            setName('')
                            setPriority('---')
                            setStatus('Supported')
                            setNewAudiences(audiences)
                            setNewInternalStakeholders(internalStakeholders)
                            setVMDescription('')
                            setNewLocalMarkets(flattenMarkets().map((market) => market.code))
                        }}
                    >
                        Create Local {valueMessageDisplayName.singular}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
})

export default CreateButton
